<template>
  <div id="CircleSelect" :class="[typeSingleRow && 'single']">
    <div class="top-row" :class="{ bordered: showDivider }" @click="emitConfiguratorToggle">
      <div class="name-row">
        <span v-if="configurationGroup.name" class="title">{{ configurationGroup.name }}</span>
        <span v-if="displayed" class="select-title">{{ displayed.value }}</span>
        <span v-else-if="selected" class="select-title">{{ selected.value }}</span>
      </div>
      <span v-if="isStackedConfigurator" class="af af-plus config-toggle" :class="[configuratorOpen ? 'open' : 'closed']"></span>
    </div>
    <div ref="configuratorOptions">
      <transition name="fadeHeight">
        <div
          v-if="(isStackedConfigurator && configuratorOpen) || !isStackedConfigurator"
          class="value-rows"
          :class="[configurationGroup.configuration.length == 1 && 'cursor-arrow', showDivider && 'bordered']"
        >
          <div v-for="(row, index) of rows" :key="index" class="row">
            <div v-for="(config, index) of row" :key="`${config.hashKey}-${config.valueId}`">
              <div class="checkbox-container" :class="[hoveredIndex == index && 'hovered']">
                <a
                  class="checkbox-circle-container"
                  tabindex="0"
                  @click="onCheckChange($event, config)"
                  @mouseover="onCheckHover($event, config, index)"
                  @mouseout="onCheckMouseOut()"
                >
                  <div
                    class="circle checkbox-circle"
                    :class="{ 'checkbox-circle-selected': (displayed && config.valueId == displayed.valueId) || (selected && config.valueId == selected.valueId) }"
                  >
                    <div class="checkbox-circle-image">
                      <ResponsiveImage :url="config.imageUrl + '?fit=max&w={width}&q={q}'" :widths="[32]" :breakpoints="[]" />
                    </div>
                  </div>
                </a>
                <div v-if="preview" class="variation-hover">
                  <ResponsiveImage :url="config.imageUrl + '?fit=max&w={width}&q={q}'" :widths="[150]" :breakpoints="[]" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </transition>
    </div>
  </div>
</template>

<script>
import smoothReflow from 'vue-smooth-reflow';

import { DeviceUtils, MediaUtils } from '@article/pixel';
import ResponsiveImage from '@/components/commons/ResponsiveImage.vue';

export default {
  name: 'CircleSelect',
  components: {
    ResponsiveImage
  },
  mixins: [smoothReflow],
  props: {
    configurationGroup: Object,
    shouldSetRow: Boolean,
    typeSingleRow: Boolean,
    preview: Boolean,
    isStackedConfigurator: {
      type: Boolean,
      default: false,
    },
    configuratorOpen: {
      type: Boolean,
      default: false,
    },
    showDivider: Boolean,
  },
  data() {
    return {
      displayed: null,
      selected: null,
      hoveredIndex: -1,
      rows: [],
    };
  },
  watch: {
    configurationGroup: {
      immediate: true,
      deep: true,
      handler() {
        this.formatSelected();
        this.setRows();
      },
    },
  },
  mounted() {
    window.addEventListener('resize', this.setRows);
  },
  destroyed() {
    window.removeEventListener('resize', this.setRows);
  },
  methods: {
    setRows() {
      if (MediaUtils.isTabletAndSmaller || !this.shouldSetRow) {
        this.rows = [this.configurationGroup.configuration];
      } else {
        let size = 10;
        if (MediaUtils.isTabletAndSmaller) size = 6;

        if (this.configurationGroup.configuration.length < size) {
          this.rows = [this.configurationGroup.configuration];
        } else {
          const cutOff = Math.ceil(this.configurationGroup.configuration.length / 2);
          this.rows = [this.configurationGroup.configuration.slice(0, cutOff), this.configurationGroup.configuration.slice(cutOff)];
        }
      }
    },
    formatSelected() {
      // eslint-disable-next-line prefer-destructuring
      this.selected = this.configurationGroup.configuration.filter((value) => value.selected)[0];
    },
    onCheckChange(event, value) {
      if (this.selected && this.selected.valueId !== value.valueId) {
        this.$emit('input', {
          id: this.configurationGroup.attributeId,
          configurationGroup: this.configurationGroup,
          value,
        });
        this.displayed = null;
      }
    },
    onCheckClick(event, value) {
      this.selected = { ...value };
    },
    onCheckHover(event, value, index) {
      if (DeviceUtils.isTablet() || DeviceUtils.isMobile()) {
        return;
      }

      this.hoveredIndex = index;
      this.displayed = { ...value };
    },
    onCheckMouseOut() {
      this.hoveredIndex = -1;
      this.displayed = null;
    },
    emitConfiguratorToggle() {
      this.$emit('toggleConfigurator');
    },
  },
};
</script>

<style lang="scss">
@import '~@article/pixel/dist/style/variables';

#CircleSelect {
  display: flex;
  flex-direction: column;
  min-width: 270px;

  .checkbox-circle-container {
    padding-left: 2px;
    padding-right: 2px;
    margin-bottom: 2px;
    display: block;
  }

  .checkbox-circle {
    height: 28px;
    width: 28px;
    border-radius: var(--circle-border-radius);
    background-color: var(--white);
    display: block;
    border: 3px solid var(--white);
    box-shadow: 0 0 0 1px var(--transparent);

    &.checkbox-circle-selected {
      box-shadow: 0 0 0 1px var(--coral);
    }
  }

  .checkbox-circle-image {
    border-radius: var(--circle-border-radius);
    overflow: hidden;
    -webkit-mask-image: -webkit-radial-gradient(white, black);
  }

  .top-row {
    display: flex;
    justify-content: space-between;
  }

  &.single {
    .variation-hover {
      display: none;
    }

    @media (min-width: $tablet-breakpoint - 1) {
      flex-direction: row;
      align-items: center;

      .top-row {
        order: 2;

        .name-row {
          .select-title {
            font-size: var(--font-size-14);
          }

          @media (min-width: $mobile-breakpoint) {
            margin-bottom: 0px;
          }
        }
      }

      .value-rows {
        margin-right: 8px;

        .color-circle {
          margin-top: 0px;
        }

        @media (min-width: $tablet-breakpoint) {
          .checkbox-container {
            &.hovered {
              .variation-hover {
                display: block;
                opacity: 1;
                margin-top: -196px;
                visibility: visible;
                transition: visibility linear 0ms;
                p {
                  display: block;
                }
              }
            }

            .variation-hover {
              opacity: 0;
              visibility: hidden;
              z-index: 70;
              position: absolute;
              width: 150px;
              height: 150px;
              margin-top: -208px;
              margin-left: 12px;
              box-shadow: 0px 1px 5px var(--grey-light);
              transition: opacity linear 100ms, margin-top linear 100ms, visibility linear 0ms 100ms;
              &:after {
                content: ' ';
                display: inline-block;
                vertical-align: middle;
                position: absolute;
                bottom: -10px;
                left: 0;
                width: 0;
                height: 0;
                border-top: 10px solid var(--white);
                border-left: 0px solid transparent;
                border-right: 58px solid transparent;
              }
              &:before {
                content: ' ';
                display: inline-block;
                vertical-align: middle;
                position: absolute;
                bottom: -11px;
                left: 0px;
                z-index: -1;
                border-top: 10px solid var(--grey-light);
                border-left: 0px solid transparent;
                border-right: 60px solid transparent;
                filter: blur(1px);
              }
              img {
                width: 140px;
                height: 140px;
                border: solid 5px var(--white);
              }
            }
          }
        }
      }
    }
  }

  .name-row {
    display: flex;
    align-items: baseline;

    .title {
      margin-right: 12px;
      text-transform: uppercase;
      letter-spacing: 2px;
      font-size: var(--font-size-14);
    }

    .select-title {
      font-family: var(--font-light);
      font-size: var(--font-size-14);
      line-height: var(--line-height-14);
    }

    @media (min-width: $mobile-breakpoint) {
      margin-bottom: 8px;
    }
  }

  .value-rows {
    &.cursor-arrow {
      a {
        cursor: initial;
      }
    }

    .row {
      display: flex;
      flex-wrap: wrap;

      .circle {
        .color-circle {
          $circleSize: 24px;
          width: $circleSize;
          height: $circleSize;
          border-radius: 50%;
          border: 3px solid white;
          overflow: auto;
          margin-bottom: 0;
        }
      }
    }
  }

  ul.value-rows {
    display: flex;
  }

  @media (max-width: $tablet-breakpoint - 1) {
    .variation-list {
      display: flex;
      flex-wrap: wrap;

      li {
        margin-top: 4px;
      }
    }
  }
}
</style>
